import { useContext } from "react";
import {
  PersonFiltersContext,
  PersonFiltersEventContext,
} from "../../../../context/FiltersContext";
import {
  QueryFilter,
  PersonFilterKeys,
  PersonRetskredsFilterSetting,
} from "../../../../interfaces/filters";
import { PersonDtoFilterInput } from "../../../../interfaces/schema";
import { f } from "../../../../util/f";
import { AccordionSubFilterItem } from "../../../accordion/Accordion";
import DefaultDropdownItem from "../../../dropdown/DefaultDropdownItem";
import SuperDropdown from "../../../dropdown/SuperDropdown";
import AppliedFilterItem from "../../../filter/AppliedFilterItem";
import { retskredse } from "../../../../util/retskredse";
import SearchIcon from "../../../icons/SearchIcon";

// helpers
const filterKey: QueryFilter<PersonFilterKeys>["filterKey"] = "retskreds";

export default function PersonRetskredsFilter() {
  // context
  const filters = useContext(PersonFiltersContext);
  const updateFilters = useContext(PersonFiltersEventContext);

  // // find active settings
  const settings: PersonRetskredsFilterSetting[] =
    filters.find((f) => f.filterKey === filterKey)?.settings || [];

  // business
  const retskredseSorted = retskredse.sort((a, b) =>
    a.navn.localeCompare(b.navn, "da-DK"),
  );

  // handlers
  function updateFilterHandler(selectedItem: { kode: number; name: string }) {
    // check if selected element already is in settings otherwise add it
    if (!settings?.some((s) => s.id === selectedItem.kode)) {
      settings.push({ id: selectedItem.kode, name: selectedItem.name });
    }

    const filterString = f<PersonDtoFilterInput>()
      .addFilter("primaerPersonFirmaRelation", {
        or: settings.map((set) => {
          return {
            firma: {
              retskredsNr: {
                eq: set.id,
              },
            },
          };
        }),
      })
      .build();

    const queryFilter: QueryFilter<
      PersonFilterKeys,
      PersonRetskredsFilterSetting[]
    > = {
      filter: filterString,
      filterKey: filterKey,
      settings: settings,
      parentFilter: "and",
    };

    updateFilters({
      event: "update",
      queryFilter: queryFilter,
    });
  }

  function removeFilterHandler(id: PersonRetskredsFilterSetting["id"]) {
    const updatedSettings: PersonRetskredsFilterSetting[] =
      settings.filter((set) => {
        return id !== set.id;
      }) || [];

    const filterString = f<PersonDtoFilterInput>()
      .addFilter("primaerPersonFirmaRelation", {
        or: updatedSettings.map((set) => {
          return {
            firma: {
              retskredsNr: {
                eq: set.id,
              },
            },
          };
        }),
      })
      .build();

    const update = updatedSettings.length > 0;

    if (update) {
      updateFilters({
        event: "update",
        queryFilter: {
          filterKey: filterKey,
          filter: filterString,
          settings: updatedSettings,
        },
      });
    } else {
      updateFilters({
        event: "delete",
        queryFilter: {
          filterKey: filterKey,
        },
      });
    }
  }

  return (
    <AccordionSubFilterItem
      title="Retskreds"
      value="retskreds"
      isActive={!!settings.length}
    >
      {settings && (
        <div className="c-applied-filter-group">
          {settings?.map((filter) => (
            <AppliedFilterItem
              key={filter.id}
              label={filter.name}
              removeFilterHandler={() => removeFilterHandler(filter.id)}
            />
          ))}
        </div>
      )}

      <SuperDropdown
        items={retskredseSorted}
        itemsIdKey="navn"
        itemToString={(item) => item?.navn ?? ""}
        itemToElement={(item) => <DefaultDropdownItem label={`${item.navn}`} />}
        filterKeys={["navn"]}
        placeholder="Søg efter retskreds ..."
        closedIcon={<SearchIcon height={14} />}
        openIcon={<SearchIcon height={14} />}
        clearInputAfterSelection={true}
        onSelectedItemEvent={(item) => {
          if (item.selectedItem?.id && item.selectedItem.navn) {
            updateFilterHandler({
              kode: item.selectedItem.id,
              name: item.selectedItem.navn,
            });
          }
        }}
      />
    </AccordionSubFilterItem>
  );
}
